import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import noop from 'lodash/noop';
import { MDText } from 'i18n-react';

import Button from '@adsk/bim360-matrix-react-button';
import SvgIcon from '@adsk/bim360-matrix-react-svg-icon';
import InlineLoader from '@adsk/bim360-matrix-react-inline-loader';
import Tooltip from '@adsk/bim360-matrix-react-tooltip';

import ellipsis from '@adsk/bim360-matrix-react-helpers/es/ellipsis';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

import docAvailable from '@adsk/bim360-matrix-react-assets/doc-available.svg';
import docUnavailable from '@adsk/bim360-matrix-react-assets/doc-not-available.svg';

import './_DocumentCell.scss';

/**
 * @class DocumentCell
 * DocumentCell is a component used to show the document name with
 * it's different states including loading and unavailable document
 **/
export class DocumentCell extends PureComponent {
  /**
   * @constructor DocumentCell
   **/
  constructor(props) {
    super(props);

    this.state = {
      showPopover: false
    };

    this._setDocumentNameRef = this._setDocumentNameRef.bind(this);
    this.handleOnShowPopoverChanged = this.handleOnShowPopoverChanged.bind(this);
  }

  _setDocumentNameRef(documentNameRef) {
    this.props.getDocumentNameRef(documentNameRef);
    this._documentNameRef = documentNameRef;
  }

  _getTruncatedName() {
    // 138px is the column width for the lowest possible media query,
    // from that we're left with 84px for the text (the rest goes to the icon and margin).
    // Use defaults from ellipsis when they're not resolved
    const computedStyle = this._documentNameRef
      ? window.getComputedStyle(this._documentNameRef)
      : {};
    const style = {
      width: parseFloat(computedStyle.width) || 84,
      fontSize: parseInt(computedStyle.fontSize) || null,
      fontWeight: parseInt(computedStyle.fontWeight) || null
    };
    return ellipsis(this.props.documentName, 5, style.width, style.fontSize, style.fontWeight);
  }

  _renderDocument(truncatedName) {
    return (
      <Button
        styleType={Button.StyleType.INLINE_LINK}
        className="DocumentCell__document-container"
        onClick={this.props.onDocumentClick}
      >
        <SvgIcon className="DocumentCell__icon" height="16px" width="16px">
          {docAvailable}
        </SvgIcon>
        <div ref={this._setDocumentNameRef} className="DocumentCell__file-name">
          {truncatedName}
        </div>
      </Button>
    );
  }

  _renderUnavailableDocument() {
    return (
      <div className="DocumentCell__na-container">
        <SvgIcon className="DocumentCell__icon" height="16px" width="16px">
          {docUnavailable}
        </SvgIcon>
        {this.props[TSymbolProp].translate('NOT_AVAILABLE')}
      </div>
    );
  }

  handleOnShowPopoverChanged({ showPopover }) {
    this.setState({ showPopover });
  }

  /**
   * Render
   * @returns {VDOM|XML}
   **/
  render() {
    if (this.props.loadingDocument) {
      return <InlineLoader className="DocumentCell__loader" />;
    }

    const popoverProps = {};
    if (this.props.unavailableDocument || this.props.documentName === undefined) {
      popoverProps.children = this._renderUnavailableDocument();
      popoverProps.content = this.props[TSymbolProp].translate('NOT_AVAILABLE');
    } else {
      const truncatedName = this._getTruncatedName();
      popoverProps.children = this._renderDocument(truncatedName);
      popoverProps.content = this.props.documentName;
      if (truncatedName === this.props.documentName) {
        popoverProps.show = false;
      }
    }

    return (
      <Tooltip
        {...popoverProps}
        className="DocumentCell__tooltip"
        container={this.props.container}
      />
    );
  }
}

DocumentCell.defaultProps = {
  getDocumentNameRef: noop,
  container: () => document.body
};

DocumentCell.propTypes = {
  /**
   * The locale to use for this component
   */
  locale: PropTypes.string,
  /**
   * The document name
   */
  documentName: PropTypes.string,
  /**
   * Is the document loading
   */
  loadingDocument: PropTypes.bool,
  /**
   * Is the document unavailable
   */
  unavailableDocument: PropTypes.bool,
  /**
   * document click event handler
   */
  onDocumentClick: PropTypes.func,
  /**
   * Callback function which returns a reference to the document name containing element
   */
  getDocumentNameRef: PropTypes.func,
  /**
   * A container to place the tooltip DOM element on (portal), defaults to the current document cell.
   */
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
};

export default withLocale(
  DocumentCell,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
